/**
 * Wizard State Manager
 * 
 * Responsible for managing wizard state and selections.
 * Follows Single Responsibility Principle by handling only state management.
 */

import { mergeDetectedContacts } from './merge-detected-contacts.js';

/**
 * Manages wizard state and data
 */
export class WizardState {
  constructor(item, contacts, lookupEmails = []) {
    this.item = item;
    const detected = Array.isArray(contacts) ? contacts : [];
    this.contacts = detected;
    this.lookupEmails = Array.isArray(lookupEmails) ? lookupEmails : [];
    
    // Current step
    this.currentStep = 1;
    this.maxSteps = 1;
    
    // Detected contacts start selected. This is a copy so later unchecks
    // do not mutate the detected list.
    this.selectedContacts = [...detected];
    this.selectedLead = null; // Single selection
    this.selectedCampaign = null; // Single selection
    this.selectedAttachments = [];
    this.selectedNotifyUsers = []; // Multi-select for sharedWith field
    this.selectedActionType = null; // Blank keeps the CRM default from email type
    this.actionTypes = [];
    this.actionTypesLoaded = false;
    
    // Cached data
    this.leadsData = new Map();
    this.campaignsData = new Map();
    this.attachmentsData = [];
    this.usersData = new Map(); // Cache for notify users

    // Signature extraction cache: null = not extracted yet, string = extracted/edited
    this.signatureText = null;
  }

  /**
   * Get cached signature text (null if not extracted yet)
   */
  getSignatureText() {
    return this.signatureText;
  }

  /**
   * Set cached signature text
   */
  setSignatureText(text) {
    this.signatureText = text;
  }

  /**
   * Get the current Outlook item
   */
  getItem() {
    return this.item;
  }

  /**
   * Get all contacts
   */
  getContacts() {
    return this.contacts;
  }

  /**
   * Replace the detected contact list
   */
  setContacts(contacts) {
    this.contacts = Array.isArray(contacts) ? contacts : [];
  }

  /**
   * Emails used to look up contacts for this item
   */
  getLookupEmails() {
    return this.lookupEmails;
  }

  /**
   * Append contacts that were not in the detected list.
   * New ids are selected. Previously unchecked ids stay unchecked.
   * @param {Array} fetchedContacts
   * @returns {Array} contacts that were added
   */
  applyFetchedContacts(fetchedContacts) {
    const result = mergeDetectedContacts(
      this.contacts,
      this.selectedContacts,
      fetchedContacts
    );
    this.contacts = result.contacts;
    this.selectedContacts = result.selectedContacts;
    return result.added;
  }

  /**
   * Get selected contacts
   */
  getSelectedContacts() {
    return this.selectedContacts;
  }

  /**
   * Set selected contacts
   */
  setSelectedContacts(contacts) {
    this.selectedContacts = contacts;
  }

  /**
   * Get selected lead
   */
  getSelectedLead() {
    return this.selectedLead;
  }

  /**
   * Set selected lead
   */
  setSelectedLead(lead) {
    this.selectedLead = lead;
  }

  /**
   * Get selected campaign
   */
  getSelectedCampaign() {
    return this.selectedCampaign;
  }

  /**
   * Set selected campaign
   */
  setSelectedCampaign(campaign) {
    this.selectedCampaign = campaign;
  }

  /**
   * Get selected attachments
   */
  getSelectedAttachments() {
    return this.selectedAttachments;
  }

  /**
   * Set selected attachments
   */
  setSelectedAttachments(attachments) {
    this.selectedAttachments = attachments;
  }

  /**
   * Get current step
   */
  getCurrentStep() {
    return this.currentStep;
  }

  /**
   * Set current step
   */
  setCurrentStep(step) {
    if (step >= 1 && step <= this.maxSteps) {
      this.currentStep = step;
      return true;
    }
    return false;
  }

  /**
   * Check if can proceed to next step
   */
  canProceedToNextStep() {
    return this.currentStep < this.maxSteps;
  }

  /**
   * Check if can go to previous step
   */
  canGoToPreviousStep() {
    return this.currentStep > 1;
  }

  /**
   * Get leads data cache
   */
  getLeadsData() {
    return this.leadsData;
  }

  /**
   * Get campaigns data cache
   */
  getCampaignsData() {
    return this.campaignsData;
  }

  /**
   * Get attachments data
   */
  getAttachmentsData() {
    return this.attachmentsData;
  }

  /**
   * Set attachments data
   */
  setAttachmentsData(attachments) {
    this.attachmentsData = attachments;
  }

  /**
   * Check if this is an appointment item
   */
  isAppointment() {
    return this.item.itemType === Office.MailboxEnums.ItemType.Appointment;
  }

  /**
   * Get selected leads as array (for API compatibility)
   */
  getSelectedLeadsArray() {
    return this.selectedLead ? [this.selectedLead] : [];
  }

  /**
   * Get selected campaigns as array (for API compatibility)
   */
  getSelectedCampaignsArray() {
    return this.selectedCampaign ? [this.selectedCampaign] : [];
  }

  /**
   * Get selected notify users (sharedWith)
   */
  getSelectedNotifyUsers() {
    return this.selectedNotifyUsers;
  }

  /**
   * Set selected notify users (sharedWith)
   */
  setSelectedNotifyUsers(users) {
    this.selectedNotifyUsers = users;
  }

  /**
   * Get users data cache
   */
  getUsersData() {
    return this.usersData;
  }

  /**
   * Selected activity type value, or null when the user skipped the dropdown.
   */
  getSelectedActionType() {
    return this.selectedActionType;
  }

  setSelectedActionType(actionType) {
    this.selectedActionType = actionType || null;
  }

  getActionTypes() {
    return this.actionTypes;
  }

  setActionTypes(actionTypes) {
    this.actionTypes = Array.isArray(actionTypes) ? actionTypes : [];
    this.actionTypesLoaded = true;
  }

  /**
   * Get sharedWith array in the format expected by the API
   * Returns array of LinkedModel objects: [{ _id: string, value: string }]
   */
  getSharedWithArray() {
    return this.selectedNotifyUsers.map(userId => {
      const user = this.usersData.get(userId);
      return {
        _id: userId,
        value: user?.value || 'Unknown User'
      };
    });
  }
}

